import React, { useState, useEffect, useCallback } from 'react';
import {
	Card,
	CardContent,
	Typography,
	CircularProgress,
	Alert,
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Chip,
	TextField,
	IconButton,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Grid,
	Tooltip,
	InputAdornment,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import FunctionsIcon from '@mui/icons-material/Functions';
import DownloadIcon from '@mui/icons-material/Download';
import StorageIcon from '@mui/icons-material/Storage';
import { Title, useNotify } from 'react-admin';
import {
	SemanticCode,
	SemanticCodeFilters,
	CreateSemanticCodeInput,
	UpdateSemanticCodeInput,
	getSemanticCodesList,
	getSemanticCode,
	createSemanticCode,
	updateSemanticCode,
	deleteSemanticCode,
	syncSemanticCodesToQdrant,
	getQdrantSemanticCodesStats,
} from '../../Services/quantiAI';

// Types de semantic codes
const SEMANTIC_TYPES = [
	{ value: 'entity', label: 'Entity (e_)', color: 'primary' as const, prefix: 'e_' },
	{ value: 'dimension', label: 'Dimension (d_)', color: 'secondary' as const, prefix: 'd_' },
	{ value: 'measure', label: 'Measure (m_)', color: 'success' as const, prefix: 'm_' },
	{ value: 'metric', label: 'Metric (mt_)', color: 'warning' as const, prefix: 'mt_' },
];

// Catégories courantes
const CATEGORIES = [
	'performance',
	'acquisition',
	'ecommerce',
	'cost',
	'engagement',
	'attribution',
	'analytics',
	'crm',
	'finance',
	'other',
];

// Agrégations possibles
const AGGREGATIONS = ['SUM', 'AVG', 'COUNT', 'MIN', 'MAX', 'NONE'];

// Unités courantes
const UNITS = ['EUR', '%', 'count', 'ratio', 'seconds', 'days', 'USD'];

// Types de connecteurs
const CONNECTOR_TYPES = ['advertiser', 'analytics', 'business', 'custom'];

// Helper pour la couleur du type
const getTypeColor = (type: string) => {
	const found = SEMANTIC_TYPES.find((t) => t.value === type);
	return found?.color || 'default';
};

// Helper pour le préfixe du type
const getTypePrefix = (type: string) => {
	const found = SEMANTIC_TYPES.find((t) => t.value === type);
	return found?.prefix || '';
};

// Dialog de création/édition
const SemanticCodeDialog = ({
	open,
	code,
	isEdit,
	onClose,
	onSave,
}: {
	open: boolean;
	code: SemanticCode | null;
	isEdit: boolean;
	onClose: () => void;
	onSave: (data: CreateSemanticCodeInput | UpdateSemanticCodeInput) => Promise<void>;
}) => {
	const [formData, setFormData] = useState<CreateSemanticCodeInput>({
		code: '',
		type: 'measure',
		name: '',
	});
	const [saving, setSaving] = useState(false);
	const [synonymsText, setSynonymsText] = useState('');

	useEffect(() => {
		if (code && isEdit) {
			setFormData({
				code: code.code,
				type: code.type,
				name: code.name,
				definition: code.definition,
				formula: code.formula,
				unit: code.unit,
				aggregation: code.aggregation,
				category: code.category,
				connector_type: code.connector_type,
			});
			setSynonymsText(code.synonyms?.join(', ') || '');
		} else {
			setFormData({
				code: '',
				type: 'measure',
				name: '',
			});
			setSynonymsText('');
		}
	}, [code, isEdit]);

	const handleTypeChange = (newType: string) => {
		const prefix = getTypePrefix(newType);
		const currentPrefix = getTypePrefix(formData.type);
		let newCode = formData.code;

		// Remplacer le préfixe si le code commence par l'ancien
		if (formData.code.startsWith(currentPrefix)) {
			newCode = prefix + formData.code.slice(currentPrefix.length);
		} else if (!formData.code) {
			newCode = prefix;
		}

		setFormData({ ...formData, type: newType, code: newCode });
	};

	const handleSave = async () => {
		if (!formData.code || !formData.name) return;
		setSaving(true);
		try {
			const synonyms = synonymsText
				.split(',')
				.map((s) => s.trim())
				.filter((s) => s);
			await onSave({ ...formData, synonyms });
			onClose();
		} finally {
			setSaving(false);
		}
	};

	// Validation du code
	const codeRegex = /^(e|d|m|mt)_[a-z][a-z0-9_]*$/;
	const isCodeValid = codeRegex.test(formData.code);

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>
				{isEdit ? `Modifier : ${code?.code}` : 'Nouveau Semantic Code'}
			</DialogTitle>
			<DialogContent>
				<Grid container spacing={2} sx={{ mt: 1 }}>
					{/* Type + Code */}
					<Grid item xs={4}>
						<FormControl fullWidth>
							<InputLabel>Type</InputLabel>
							<Select
								value={formData.type}
								label="Type"
								onChange={(e) => handleTypeChange(e.target.value)}
								disabled={isEdit}
							>
								{SEMANTIC_TYPES.map((t) => (
									<MenuItem key={t.value} value={t.value}>
										<Chip
											label={t.label}
											size="small"
											color={t.color}
											sx={{ mr: 1 }}
										/>
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={8}>
						<TextField
							fullWidth
							label="Code"
							value={formData.code}
							onChange={(e) =>
								setFormData({ ...formData, code: e.target.value.toLowerCase() })
							}
							disabled={isEdit}
							error={formData.code.length > 0 && !isCodeValid}
							helperText={
								formData.code.length > 0 && !isCodeValid
									? 'Format: {e|d|m|mt}_nom_en_snake_case'
									: 'Ex: mt_roas, m_spend, d_date, e_campaign_id'
							}
							InputProps={{
								sx: { fontFamily: 'monospace' },
							}}
						/>
					</Grid>

					{/* Nom + Catégorie */}
					<Grid item xs={8}>
						<TextField
							fullWidth
							label="Nom"
							value={formData.name}
							onChange={(e) => setFormData({ ...formData, name: e.target.value })}
							placeholder="Nom lisible (ROAS, Dépenses, etc.)"
							required
						/>
					</Grid>
					<Grid item xs={4}>
						<FormControl fullWidth>
							<InputLabel>Catégorie</InputLabel>
							<Select
								value={formData.category || ''}
								label="Catégorie"
								onChange={(e) =>
									setFormData({ ...formData, category: e.target.value || undefined })
								}
							>
								<MenuItem value="">
									<em>Aucune</em>
								</MenuItem>
								{CATEGORIES.map((c) => (
									<MenuItem key={c} value={c}>
										{c}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>

					{/* Définition */}
					<Grid item xs={12}>
						<TextField
							fullWidth
							multiline
							rows={2}
							label="Définition"
							value={formData.definition || ''}
							onChange={(e) =>
								setFormData({ ...formData, definition: e.target.value || undefined })
							}
							placeholder="Description métier du code"
						/>
					</Grid>

					{/* Formule (pour les métriques) */}
					{(formData.type === 'metric' || formData.formula) && (
						<Grid item xs={12}>
							<TextField
								fullWidth
								label="Formule"
								value={formData.formula || ''}
								onChange={(e) =>
									setFormData({ ...formData, formula: e.target.value || undefined })
								}
								placeholder="m_revenue / m_spend (pour les métriques calculées)"
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<FunctionsIcon />
										</InputAdornment>
									),
									sx: { fontFamily: 'monospace' },
								}}
							/>
						</Grid>
					)}

					{/* Unité + Agrégation */}
					<Grid item xs={4}>
						<FormControl fullWidth>
							<InputLabel>Unité</InputLabel>
							<Select
								value={formData.unit || ''}
								label="Unité"
								onChange={(e) =>
									setFormData({ ...formData, unit: e.target.value || undefined })
								}
							>
								<MenuItem value="">
									<em>Aucune</em>
								</MenuItem>
								{UNITS.map((u) => (
									<MenuItem key={u} value={u}>
										{u}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={4}>
						<FormControl fullWidth>
							<InputLabel>Agrégation</InputLabel>
							<Select
								value={formData.aggregation || ''}
								label="Agrégation"
								onChange={(e) =>
									setFormData({ ...formData, aggregation: e.target.value || undefined })
								}
							>
								<MenuItem value="">
									<em>Aucune</em>
								</MenuItem>
								{AGGREGATIONS.map((a) => (
									<MenuItem key={a} value={a}>
										{a}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={4}>
						<FormControl fullWidth>
							<InputLabel>Connecteur</InputLabel>
							<Select
								value={formData.connector_type || ''}
								label="Connecteur"
								onChange={(e) =>
									setFormData({
										...formData,
										connector_type: e.target.value || undefined,
									})
								}
							>
								<MenuItem value="">
									<em>Global (tous)</em>
								</MenuItem>
								{CONNECTOR_TYPES.map((ct) => (
									<MenuItem key={ct} value={ct}>
										{ct}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>

					{/* Synonymes */}
					<Grid item xs={12}>
						<TextField
							fullWidth
							label="Synonymes"
							value={synonymsText}
							onChange={(e) => setSynonymsText(e.target.value)}
							placeholder="dépenses, coûts, budget (séparés par des virgules)"
							helperText="Synonymes pour améliorer le matching RAG"
						/>
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Annuler</Button>
				<Button
					variant="contained"
					onClick={handleSave}
					disabled={saving || !formData.code || !formData.name || !isCodeValid}
				>
					{saving ? <CircularProgress size={20} /> : isEdit ? 'Mettre à jour' : 'Créer'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

// Dialog de confirmation de suppression
const DeleteConfirmDialog = ({
	open,
	code,
	onClose,
	onConfirm,
}: {
	open: boolean;
	code: SemanticCode | null;
	onClose: () => void;
	onConfirm: () => Promise<void>;
}) => {
	const [deleting, setDeleting] = useState(false);

	const handleConfirm = async () => {
		setDeleting(true);
		try {
			await onConfirm();
			onClose();
		} finally {
			setDeleting(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogTitle>Confirmer la suppression</DialogTitle>
			<DialogContent>
				<Typography>
					Supprimer le semantic code <strong>{code?.code}</strong> ?
				</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
					Cette action est irréversible.
				</Typography>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Annuler</Button>
				<Button
					variant="contained"
					color="error"
					onClick={handleConfirm}
					disabled={deleting}
				>
					{deleting ? <CircularProgress size={20} /> : 'Supprimer'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

// Composant principal
export const SemanticCodesList = () => {
	const [codes, setCodes] = useState<SemanticCode[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [search, setSearch] = useState('');
	const [typeFilter, setTypeFilter] = useState<string>('');
	const [categoryFilter, setCategoryFilter] = useState<string>('');
	const [categories, setCategories] = useState<string[]>([]);

	// Dialogs
	const [editDialog, setEditDialog] = useState<{
		open: boolean;
		code: SemanticCode | null;
		isEdit: boolean;
	}>({ open: false, code: null, isEdit: false });
	const [deleteDialog, setDeleteDialog] = useState<{
		open: boolean;
		code: SemanticCode | null;
	}>({ open: false, code: null });

	// Qdrant sync
	const [syncing, setSyncing] = useState(false);
	const [qdrantStats, setQdrantStats] = useState<{ total_points: number } | null>(null);

	const notify = useNotify();

	// Charger les codes
	const loadCodes = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const filters: SemanticCodeFilters = {};
			if (typeFilter) filters.type = typeFilter;
			if (categoryFilter) filters.category = categoryFilter;

			const response = await getSemanticCodesList(filters);
			let filtered = response.codes || [];

			// Filtre local sur le search
			if (search) {
				const searchLower = search.toLowerCase();
				filtered = filtered.filter(
					(c) =>
						c.code.toLowerCase().includes(searchLower) ||
						c.name.toLowerCase().includes(searchLower) ||
						c.definition?.toLowerCase().includes(searchLower) ||
						c.synonyms?.some((s) => s.toLowerCase().includes(searchLower))
				);
			}

			setCodes(filtered);

			// Extraire les catégories uniques
			const allCats = response.codes
				?.map((c) => c.category)
				.filter((c): c is string => !!c) || [];
			setCategories([...new Set(allCats)].sort());
		} catch (err: any) {
			setError(err.message || 'Erreur lors du chargement');
		} finally {
			setLoading(false);
		}
	}, [typeFilter, categoryFilter, search]);

	useEffect(() => {
		loadCodes();
	}, [loadCodes]);

	// Charger les stats Qdrant
	useEffect(() => {
		const loadQdrantStats = async () => {
			try {
				const stats = await getQdrantSemanticCodesStats();
				setQdrantStats(stats);
			} catch {
				// Silently ignore - Qdrant might not be configured
			}
		};
		loadQdrantStats();
	}, []);

	// Sync Qdrant
	const handleSyncQdrant = async () => {
		setSyncing(true);
		try {
			const result = await syncSemanticCodesToQdrant();
			if (result.errors > 0) {
				notify(`Sync partiel: ${result.indexed}/${result.total_codes} indexés (${result.errors} erreurs)`, {
					type: 'warning',
				});
			} else {
				notify(`Sync Qdrant terminé: ${result.indexed} codes indexés`, { type: 'success' });
			}
			// Refresh stats
			const stats = await getQdrantSemanticCodesStats();
			setQdrantStats(stats);
		} catch (err: any) {
			notify(err.message || 'Erreur sync Qdrant', { type: 'error' });
		} finally {
			setSyncing(false);
		}
	};

	// Export CSV
	const handleExportCSV = () => {
		const headers = [
			'code',
			'type',
			'name',
			'definition',
			'formula',
			'unit',
			'synonyms',
			'aggregation',
			'category',
			'connector_type',
		];

		const escapeCSV = (value: string | undefined | null): string => {
			if (value === null || value === undefined) return '';
			const str = String(value);
			if (str.includes(',') || str.includes('"') || str.includes('\n')) {
				return `"${str.replace(/"/g, '""')}"`;
			}
			return str;
		};

		const rows = codes.map((c) => [
			escapeCSV(c.code),
			escapeCSV(c.type),
			escapeCSV(c.name),
			escapeCSV(c.definition),
			escapeCSV(c.formula),
			escapeCSV(c.unit),
			escapeCSV(c.synonyms?.join(', ')),
			escapeCSV(c.aggregation),
			escapeCSV(c.category),
			escapeCSV(c.connector_type),
		]);

		const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = `semantic_codes_${new Date().toISOString().split('T')[0]}.csv`;
		link.click();
		URL.revokeObjectURL(url);
	};

	// Handlers
	const handleCreate = () => {
		setEditDialog({ open: true, code: null, isEdit: false });
	};

	const handleEdit = async (code: SemanticCode) => {
		try {
			const detail = await getSemanticCode(code.code);
			setEditDialog({ open: true, code: detail, isEdit: true });
		} catch (err: any) {
			notify(err.message || 'Erreur', { type: 'error' });
		}
	};

	const handleDelete = (code: SemanticCode) => {
		setDeleteDialog({ open: true, code });
	};

	const handleSaveCode = async (data: CreateSemanticCodeInput | UpdateSemanticCodeInput) => {
		try {
			if (editDialog.isEdit && editDialog.code) {
				await updateSemanticCode(editDialog.code.code, data as UpdateSemanticCodeInput);
				notify('Semantic code mis à jour', { type: 'success' });
			} else {
				await createSemanticCode(data as CreateSemanticCodeInput);
				notify('Semantic code créé', { type: 'success' });
			}
			loadCodes();
		} catch (err: any) {
			notify(err.message || 'Erreur', { type: 'error' });
			throw err;
		}
	};

	const handleConfirmDelete = async () => {
		if (!deleteDialog.code) return;
		try {
			await deleteSemanticCode(deleteDialog.code.code);
			notify('Semantic code supprimé', { type: 'success' });
			loadCodes();
		} catch (err: any) {
			notify(err.message || 'Erreur', { type: 'error' });
			throw err;
		}
	};

	// Stats
	const stats = {
		total: codes.length,
		entity: codes.filter((c) => c.type === 'entity').length,
		dimension: codes.filter((c) => c.type === 'dimension').length,
		measure: codes.filter((c) => c.type === 'measure').length,
		metric: codes.filter((c) => c.type === 'metric').length,
	};

	return (
		<Card>
			<Title title="Quanti AI - Semantic Codes" />
			<CardContent>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
					<Box>
						<Typography variant="h5">Semantic Codes</Typography>
						<Typography variant="body2" color="text.secondary">
							Glossaire central de la convention QUANTI Semantic Layer
						</Typography>
					</Box>
					<Box sx={{ display: 'flex', gap: 1 }}>
						<Tooltip title={qdrantStats ? `${qdrantStats.total_points} points indexés` : 'Sync vers Qdrant pour RAG'}>
							<Button
								variant="outlined"
								color="secondary"
								startIcon={syncing ? <CircularProgress size={16} /> : <StorageIcon />}
								onClick={handleSyncQdrant}
								disabled={syncing || codes.length === 0}
							>
								{syncing ? 'Sync...' : 'Sync Qdrant'}
							</Button>
						</Tooltip>
						<Button
							variant="outlined"
							startIcon={<DownloadIcon />}
							onClick={handleExportCSV}
							disabled={codes.length === 0}
						>
							Export CSV
						</Button>
						<Button
							variant="contained"
							startIcon={<AddIcon />}
							onClick={handleCreate}
						>
							Nouveau Code
						</Button>
					</Box>
				</Box>

				{/* Stats rapides */}
				<Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
					<Chip label={`Total: ${stats.total}`} variant="outlined" />
					<Chip label={`e_: ${stats.entity}`} color="primary" variant="outlined" />
					<Chip label={`d_: ${stats.dimension}`} color="secondary" variant="outlined" />
					<Chip label={`m_: ${stats.measure}`} color="success" variant="outlined" />
					<Chip label={`mt_: ${stats.metric}`} color="warning" variant="outlined" />
					{qdrantStats && (
						<Chip
							icon={<StorageIcon />}
							label={`Qdrant: ${qdrantStats.total_points}`}
							color="info"
							variant="outlined"
						/>
					)}
				</Box>

				{/* Filtres */}
				<Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
					<TextField
						size="small"
						placeholder="Rechercher..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon />
								</InputAdornment>
							),
						}}
						sx={{ minWidth: 250 }}
					/>

					<FormControl size="small" sx={{ minWidth: 150 }}>
						<InputLabel>Type</InputLabel>
						<Select
							value={typeFilter}
							label="Type"
							onChange={(e) => setTypeFilter(e.target.value)}
						>
							<MenuItem value="">
								<em>Tous</em>
							</MenuItem>
							{SEMANTIC_TYPES.map((t) => (
								<MenuItem key={t.value} value={t.value}>
									{t.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl size="small" sx={{ minWidth: 150 }}>
						<InputLabel>Catégorie</InputLabel>
						<Select
							value={categoryFilter}
							label="Catégorie"
							onChange={(e) => setCategoryFilter(e.target.value)}
						>
							<MenuItem value="">
								<em>Toutes</em>
							</MenuItem>
							{categories.map((c) => (
								<MenuItem key={c} value={c}>
									{c}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Box>

				{/* Info convention */}
				<Alert severity="info" sx={{ mb: 2 }}>
					<Typography variant="subtitle2" gutterBottom>
						Convention QUANTI Semantic Layer
					</Typography>
					<Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
						<Typography variant="body2">
							<strong>e_</strong> = Entités (IDs)
						</Typography>
						<Typography variant="body2">
							<strong>d_</strong> = Dimensions (attributs)
						</Typography>
						<Typography variant="body2">
							<strong>m_</strong> = Mesures (valeurs brutes)
						</Typography>
						<Typography variant="body2">
							<strong>mt_</strong> = Métriques (calculées)
						</Typography>
					</Box>
				</Alert>

				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}

				{loading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
						<CircularProgress />
					</Box>
				) : codes.length > 0 ? (
					<TableContainer component={Paper}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Code</TableCell>
									<TableCell>Type</TableCell>
									<TableCell>Nom</TableCell>
									<TableCell>Définition</TableCell>
									<TableCell>Formule</TableCell>
									<TableCell>Unité</TableCell>
									<TableCell>Catégorie</TableCell>
									<TableCell>Scope</TableCell>
									<TableCell align="right">Actions</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{codes.map((code) => (
									<TableRow key={code.id} hover>
										<TableCell>
											<Typography
												variant="body2"
												sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}
											>
												{code.code}
											</Typography>
										</TableCell>
										<TableCell>
											<Chip
												label={code.type}
												size="small"
												color={getTypeColor(code.type)}
											/>
										</TableCell>
										<TableCell>{code.name}</TableCell>
										<TableCell>
											<Tooltip title={code.definition || ''} placement="top">
												<Typography
													variant="body2"
													sx={{
														maxWidth: 200,
														overflow: 'hidden',
														textOverflow: 'ellipsis',
														whiteSpace: 'nowrap',
													}}
												>
													{code.definition || '-'}
												</Typography>
											</Tooltip>
										</TableCell>
										<TableCell>
											{code.formula ? (
												<Tooltip title={code.formula} placement="top">
													<Typography
														variant="body2"
														sx={{
															fontFamily: 'monospace',
															maxWidth: 100,
															overflow: 'hidden',
															textOverflow: 'ellipsis',
															whiteSpace: 'nowrap',
														}}
													>
														{code.formula}
													</Typography>
												</Tooltip>
											) : (
												'-'
											)}
										</TableCell>
										<TableCell>{code.unit || '-'}</TableCell>
										<TableCell>
											{code.category ? (
												<Chip label={code.category} size="small" variant="outlined" />
											) : (
												'-'
											)}
										</TableCell>
										<TableCell>
											{code.connector_type ? (
												<Chip
													label={code.connector_type}
													size="small"
													color="info"
													variant="outlined"
												/>
											) : (
												<Typography variant="body2" color="text.secondary">
													Global
												</Typography>
											)}
										</TableCell>
										<TableCell align="right">
											<IconButton size="small" onClick={() => handleEdit(code)}>
												<EditIcon fontSize="small" />
											</IconButton>
											<IconButton
												size="small"
												onClick={() => handleDelete(code)}
												color="error"
											>
												<DeleteIcon fontSize="small" />
											</IconButton>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				) : (
					<Alert severity="info">
						Aucun semantic code trouvé.
						{!typeFilter && !categoryFilter && !search && (
							<> Cliquez sur "Nouveau Code" pour en créer un.</>
						)}
					</Alert>
				)}
			</CardContent>

			{/* Dialogs */}
			<SemanticCodeDialog
				open={editDialog.open}
				code={editDialog.code}
				isEdit={editDialog.isEdit}
				onClose={() => setEditDialog({ open: false, code: null, isEdit: false })}
				onSave={handleSaveCode}
			/>

			<DeleteConfirmDialog
				open={deleteDialog.open}
				code={deleteDialog.code}
				onClose={() => setDeleteDialog({ open: false, code: null })}
				onConfirm={handleConfirmDelete}
			/>
		</Card>
	);
};
